
import { first } from 'rxjs/operators';
import { Component, OnInit, OnDestroy } from "@angular/core";
import { BehaviorSubject, Subscription } from "rxjs";
import * as jp from "jsonpath/jsonpath";
//import { GridOptions, ColDef, AllModules } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";


import { BaseControlComponent } from "../base-control/base-control.component";
import { LoaderService } from "../../../common/services/loader.service";
import { QueryService } from "../../../common/services/query.service";
import { DataQuery, queryResultsHaveChanged } from "../../../common/models/query.model";
import { StateType } from "../../../common/models/flow.dto";
import { GridFormatterService } from '../../../common/services/grid-formatter.service';
import { ContextInteractionService } from '../../services/context-interaction.service';

@Component({
    selector: "app-data-query-button",
    templateUrl: "./data-query-button.component.html",
    styleUrls: ["./data-query-button.component.scss"],
    standalone: false
})
export class DataQueryButtonComponent extends BaseControlComponent implements OnInit, OnDestroy {

    static componentName = "DataQueryButtonComponent";

    dataResults: any;
    displayResults: string = "";
    enabledSubscription: Subscription;
    errorMessage: string = "";
	gridOptions: GridOptions;
	gridApi?: GridApi;
    queryResultsChanged: boolean = false;
    rowSelection: string = "multiple";
    queryRun$ = new BehaviorSubject<boolean>(false);
    tableColumnDefinitions: ColDef[] = [];
    tableData = [];
    textResults: string = "";
    tmpTabledata = [];


    get disableQuery(): boolean {
        return this.isEdit || !this.isEnabled;
    }

    get showButton(): boolean {
        return !this.componentData.config.runOnInit;
    }


    constructor(public readonly service: QueryService, public readonly loader: LoaderService,
                public readonly gridFormatterService: GridFormatterService, public readonly contextService: ContextInteractionService) {
        super(contextService);
    }

    ngOnInit() {
        super.ngOnInit();
        if (!this.componentData.config) {
            this.componentData.config = {
                "text": "Run Query",
                "display": "selectable",
                "limitSelections": "false"
            }
        }
        if (!this.componentData.config.text) {
            this.componentData.config.text = "Run Query";
        }
        if (!this.componentData.config.display) {
            this.componentData.config.display = "selectable";
            this.componentData.config.limitSelections = "false";
        }

        if (this.componentData.config.display === "selectable" && !this.enableEditing) {
            this.componentData.config.display = "table";
        }

        if (this.componentData.config.display === "text") {
            if (!this.componentData.config.prefixText) {
                this.componentData.config.prefixText = "";
            }

            if (!this.componentData.config.suffixText) {
                this.componentData.config.suffixText = "";
            }
        } else {
            this.loadColumnDefinitions();
			this.gridOptions = this.gridFormatterService.BuildDefaultGridOptionsWithColumns(this.tableColumnDefinitions, false, true, true, true);
			this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
        }
        if (this.isEdit) {
            if (this.componentData.config && this.componentData.config.query) {
                this.service.getByName(this.componentData.config.query.name).subscribe(q => {
                    this.queryResultsChanged = queryResultsHaveChanged(this.componentData.config.query, q);
                    if (this.queryResultsChanged) {
                        setTimeout(() => {
                            this.queryResultsChanged = false;
                        },
                            2000);
                    }
                },
                    error => {
                        console.log(`Error loading current version of query: ${error}`);
                    });
            }
        } else {
            const state = this.flowComponent.workflow.findCurrentState();
            if (this.componentData.config.runOnInit) {
                if (this.isEnabled) {
                    //run the query fresh on load for input forms, but show what the submitter would have seen for review forms
                    if (state && state.stateType === StateType.InputForm) { 
                        this.runQuery();
                    } else {
                        this.restoreData();
                    }
                } else {

                    this.enabledSubscription = this.enabledObs$.pipe()
                        .subscribe(
                            (enabledValue) => {                                
                                if (enabledValue) {
                                    this.runQuery();
                                } else {
                                    this.storeResults(null);
                                }
                            },
                            (error) => {
                                console.error(error);
                            }
                        );
                }
                
            } else { //restore any existing data from previous runs
                this.restoreData();
            }
        }

    }

    ngOnDestroy() {
        if (this.enabledSubscription) this.enabledSubscription.unsubscribe();
    }

    runQuery(): void {
        this.storeResults(null);
        if (this.errorMessage) {
            this.errorMessage = "";
        }
        if (this.tableData.length > 0) {
            this.tableData = [];
        }

        this.loader.toggleLoader(true);
        this.service.runQuery(this.componentData.config.query as DataQuery, this).pipe(first()).subscribe(data => {
            this.queryRun$.next(true);
            this.formatResults(data);
            this.loader.toggleLoader(false);
            this.change();
            if (this.gridApi) {
				this.gridApi.sizeColumnsToFit();
				this.gridApi.autoSizeAllColumns();
                
            }                    
        }, (error) => {
                if (error.status === 404) {
                    this.errorMessage = (this.componentData.config.display.toLocaleLowerCase() !== "context") ?  error.error : "";
                } else {
                    if (this.componentData.config.runOnInit) {
                        this.errorMessage = "An error has occurred in processing the query.";
                    } else {
                        this.errorMessage = "An error has occurred in processing your query.  Please verify your input and try again.";
                    }
                }
                this.queryRun$.next(true);
                this.loader.toggleLoader(false);
                this.change();
                if (this.gridApi) {
                    this.gridApi.sizeColumnsToFit();
                }            
        });
    }

    formatResults(results: any): void {
        if (this.componentData.config.display === "text") {
            this.formatResultsAsText(results);
        } else {
            this.formatResultsAsDataTable(results);
        }
        this.storeResults(results);
    }

    formatResultsAsText(results: any): void {
        if (results.length === undefined) {
            results = [results];
        }
        if (this.componentData.config.prefixText.length > 0) {
            this.displayResults = this.componentData.config.prefixText;
        }
        for (let r = 0; r < this.componentData.config.query.resultValues.length; r++) {
            for (let t = 0; t < results.length; t++) {
                const node = jp.value(results, `$${`[${t}]`}`);
                const keys = Object.keys(node);
                let value = undefined;
                for (let k = 0; k < keys.length; k++) {
                    const key = keys[k];
                    if (key === this.componentData.config.query.resultValues[r].returnValue) {
                        value = Object.values(node)[k];
                    }
                    if (value !== undefined)
                        break;
                }
                if (t > 0) {
                    this.textResults = `${this.textResults}, ${value}`;
                } else if (this.textResults.length > 0) {
                    this.textResults = `${this.textResults} ${value}`;
                } else {
                    this.textResults = value;
                }
            }
        }
        if (this.componentData.config.suffixText.length > 0) {
            this.displayResults = `${this.displayResults} ${this.textResults} ${this.componentData.config.suffixText}`;
        } else {
            this.displayResults = `${this.displayResults} ${this.textResults}`;
        }
    }

    formatResultsAsDataTable(results: any): void {
        if (this.componentData.config.display === "selectable") {
            this.dataResults = results;
            this.setContextValue([]);
        } else {
            this.setContextValue(results);
        }
        let datum = {};

        //Reset query results
        this.tmpTabledata = [];
        for (let v = 0; v < this.componentData.config.query.resultValues.length; v++) {
            for (let t = 0; t < results.length; t++) {
                const node = jp.value(results, `$${`[${t}]`}`);
                const keys = Object.keys(node);
                const nameValue = this.componentData.config.query.resultValues[v].returnValue;

                let valueFound: boolean = false;
                for (let k = 0; k < keys.length; k++) {
                    const key = keys[k];
                    if (key === this.componentData.config.query.resultValues[v].returnValue) {
                        datum[nameValue] = Object.values(node)[k];
                        valueFound = true;
                    }
                    if (valueFound) break;
                }
                this.tmpTabledata.push(datum);
            }

        }
        this.tableData = this.tmpTabledata.filter((n, i) =>
            this.tmpTabledata.indexOf(n) === i);

    }

    loadColumnDefinitions(): void {
        if (this.componentData.config.query && this.componentData.config.query.resultValues) {
            for (let v = 0; v < this.componentData.config.query.resultValues.length; v++) {
                const colDef = {
                    headerName: this.componentData.config.query.resultValues[v].returnValueName,
                    headerTooltip: this.componentData.config.query.resultValues[v].returnValueName,
                    field: this.componentData.config.query.resultValues[v].returnValue,
                    tooltipField: this.componentData.config.query.resultValues[v].returnValue,
                    
                } as ColDef;
                if (v === 0 && this.componentData.config.display === "selectable") {
                    colDef.checkboxSelection = true;
                }
                this.tableColumnDefinitions.push(colDef);
            }
        }
    }

    onSelectionChanged(e: Event): void {
        const currentSelection = this.getContextValue();
        const newSelection = this.gridApi.getSelectedRows();
        const nodes = this.gridApi.getSelectedNodes();
        if (this.componentData.config.limitSelections === "false") {
            //no limit on selections, just save and return
            this.setContextValue(newSelection);
            return;
        }
        if (newSelection.length < currentSelection.length) {
            //change is removing a row, just save and return
            this.setContextValue(newSelection);
            return;
        }
        const limit = parseInt(this.componentData.config.maxSelections, 10);
        if (newSelection.length > limit) {
            for (let n = 0; n < nodes.length; n++) {
                const currentIndex = currentSelection.findIndex(s => nodes[n].data === s);
                if (currentIndex === -1) {
                    nodes[n].setSelected(false);
                }
            }

            this.errorMessage =
                `Selections limited to ${limit} selections or fewer.  Please de-select one or more rows before selecting additional rows.`;

        } else {
            this.setContextValue(newSelection);
        }
    }

    restoreData(): void {
        const data = this.getContext("results");
        if (data) {
            this.formatResults(data);
            this.queryRun$.next(true);
        }
    }

    storeResults(results: any): void {
        if (results === null) {
            this.textResults = "";
            this.displayResults = "";
            this.dataResults = [];
        }
        if (this.componentData.config.display === "text") {
            this.setContextValue(this.textResults);
        } else if (this.componentData.config.display === "selectable") {
            this.setContextValue(this.dataResults);
        } else {
            this.setContextValue(results);
        }
        this.storeContext(results, "results");
    }
}
